import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { WorkloadContextData, WorkloadData, WorkloadProviderProps } from './types';
import {
  TWO_WEEKLY_LIMIT,
  MONTHLY_LIMIT,
  initialState,
  WEEK_LENGTH,
  EMPTY_HIDDEN_WEEKEND,
} from './constants';
import {
  countHiddenWeekendSchedules,
  countSchedulesByDay,
  generateDays,
  generateWeekColumns,
  getDayKey,
  getFirstDay,
  getLastDay,
  getSidebarTab,
} from './helpers';
import { useGetWorkloadCalendarSidebar } from '@services/workload/calendar/sidebar';
import { useGetWorkloadCalendar } from '@services/workload/calendar';
import { differenceInCalendarDays } from 'date-fns';
import { useGetWorkloadMonthlyCalendar } from '@services/workload/calendar/monthly';
import {
  computeMonthlyDrafts,
  filterWorkloadMonthlyRecords,
} from '@services/workload/calendar/hiddenRecords';
import { useQueryParams } from '@hooks/queryParams';
import { useWorkloadFilterParams } from '@hooks/workload/filterParams';
import { useAppContext } from '@contexts/app';
import { toZonedTime } from 'date-fns-tz';
import { useInView } from 'react-intersection-observer';
import {
  getWorkloadDisplayOptionsSettingsEndpoint,
  getDisplayOptionsSettings,
} from '@components/workload/drawer/displayOptions/helpers';

// prefetch the next page before the sentinel is actually scrolled into view
const JOBS_SIDEBAR_PREFETCH_MARGIN = '0px 0px 200px 0px';

const WorkloadContext = createContext<WorkloadContextData | Record<string, never>>({});

export const WorkloadProvider = ({ children }: WorkloadProviderProps) => {
  const { tenant, displayOptions } = useAppContext();
  const [state, setState] = useState<WorkloadData>(initialState);
  const { visibleDays, date, expandedWeekendDays } = state;

  const { params } = useQueryParams();
  const { filterParams, filterKey } = useWorkloadFilterParams();
  const { type, searchSidebar, divisionIds, team, workflowIds, taskTypes } = params;

  const isTwoWeekly = type === 'two-week';
  const { showHidden } = state;

  const [jobsPage, setJobsPage] = useState(1);
  const [jobsRecords, setJobsRecords] = useState<unknown[]>([]);

  const settings = useMemo(
    () =>
      getDisplayOptionsSettings(
        displayOptions || [],
        getWorkloadDisplayOptionsSettingsEndpoint(type),
      ),
    [displayOptions, type],
  );

  const weekSettings = useMemo(
    () => getDisplayOptionsSettings(displayOptions || [], 'weekCalendar'),
    [displayOptions],
  );

  const baseWeekColumns = useMemo(
    () =>
      visibleDays ? generateWeekColumns(visibleDays, date, weekSettings, expandedWeekendDays) : [],
    [visibleDays, date, weekSettings, expandedWeekendDays],
  );

  const weekDates = useMemo(
    () => baseWeekColumns.filter((column) => !column.collapsed).map((column) => column.date),
    [baseWeekColumns],
  );

  const first = useMemo(() => getFirstDay(date, type), [date, type]);
  const last = useMemo(() => getLastDay(date, type), [date, type]);
  const difference = differenceInCalendarDays(last, first);

  const calendar = useGetWorkloadCalendar(date, weekDates);
  const monthly = useGetWorkloadMonthlyCalendar(first);
  const jobs = useGetWorkloadCalendarSidebar('jobs', jobsPage);
  const specialties = useGetWorkloadCalendarSidebar('specialties');

  const jobsPagy = jobs.data?.pagy;
  const isJobsLastPage = Boolean(jobsPagy) && jobsPagy?.next == null;
  const jobsFilterKey = JSON.stringify({
    searchSidebar,
    divisionIds,
    team,
    workflowIds,
    taskTypes,
  });

  const { ref: jobsLoaderRef, inView: jobsInView } = useInView({
    skip: jobs.isLoading,
    rootMargin: JOBS_SIDEBAR_PREFETCH_MARGIN,
  });

  const setData = useCallback((prop: keyof WorkloadData, value: unknown): void => {
    setState((prev) => ({ ...prev, [prop]: value }));
  }, []);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      sidebarTab: getSidebarTab(settings),
    }));
  }, [settings]);

  useEffect(() => {
    if (!tenant.zone) return;
    setData('date', toZonedTime(new Date(), tenant.zone));
  }, [tenant.zone, setData]);

  useEffect(() => {
    setData('dates', weekDates);
  }, [weekDates, setData]);

  useEffect(() => {
    const daysInView = new Set(baseWeekColumns.map((column) => getDayKey(column.date)));
    setState((prev) => {
      const expanded = prev.expandedWeekendDays.filter((day) => daysInView.has(day));
      if (expanded.length === prev.expandedWeekendDays.length) return prev;
      return { ...prev, expandedWeekendDays: expanded };
    });
  }, [baseWeekColumns]);

  useEffect(() => {
    setState((prev) =>
      prev.expandedWeekendDays.length ? { ...prev, expandedWeekendDays: [] } : prev,
    );
  }, [type]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      monthly: {
        ...prev.monthly,
        dates: generateDays(difference + 1, first, settings),
        weekLength:
          WEEK_LENGTH - (settings?.weekendsSaturday ? 0 : 1) - (settings?.weekendsSunday ? 0 : 1),
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, type, settings]);

  useEffect(() => {
    setJobsPage(1);
    setJobsRecords([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsFilterKey]);

  useEffect(() => {
    setJobsRecords((prev) =>
      jobsPage === 1 ? jobs.data?.records || [] : [...prev, ...(jobs.data?.records || [])],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.data?.records, jobsPage]);

  useEffect(() => {
    if (!jobsInView || !jobsPagy?.next) return;
    const timeout = setTimeout(() => setJobsPage(jobsPagy.next as number), 400);
    return () => clearTimeout(timeout);
  }, [jobsInView, jobsPagy?.next]);

  const { visibleRecords, hiddenCount, hiddenDaysCount } = useMemo(
    () => filterWorkloadMonthlyRecords(monthly.data?.records || {}, filterParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthly.data?.records, filterKey],
  );

  const monthlyDrafts = useMemo(() => computeMonthlyDrafts(visibleRecords), [visibleRecords]);

  const hiddenWeekend = useMemo(() => {
    if (type !== 'two-week' && type !== 'month') return EMPTY_HIDDEN_WEEKEND;

    const records = showHidden ? monthly.data?.records || {} : visibleRecords;
    return countHiddenWeekendSchedules(
      Object.entries(records).flatMap(([startDate, schedules]) =>
        schedules.map(({ id }) => ({ id, startDate })),
      ),
      settings,
    );
  }, [type, showHidden, settings, monthly.data?.records, visibleRecords]);

  const weekColumns = useMemo(() => {
    if (!baseWeekColumns.some((column) => column.collapsed)) return baseWeekColumns;

    const foremen = showHidden ? calendar.data?.records || [] : calendar.allDatesVisibleRecords;
    const withoutTeam = showHidden
      ? calendar.data?.withoutTeam || []
      : calendar.allDatesVisibleWithoutTeam;
    const counts = countSchedulesByDay([
      ...foremen.flatMap((foreman) => foreman.schedules),
      ...withoutTeam,
    ]);

    return baseWeekColumns.flatMap((column) => {
      if (!column.collapsed) return [column];
      const hiddenCount = counts[getDayKey(column.date)] || 0;
      return hiddenCount ? [{ ...column, hiddenCount }] : [];
    });
  }, [
    baseWeekColumns,
    showHidden,
    calendar.data?.records,
    calendar.data?.withoutTeam,
    calendar.allDatesVisibleRecords,
    calendar.allDatesVisibleWithoutTeam,
  ]);

  const contextValue = useMemo<WorkloadContextData>(
    () => ({
      ...state,
      setState,
      sidebar: {
        jobs: {
          isLoading: jobs.isLoading,
          records: jobsRecords,
          pagy: jobsPagy,
          loader: {
            ref: !isJobsLastPage ? jobsLoaderRef : undefined,
            visible: (jobs.isLoading && jobsPage !== 1) || !isJobsLastPage,
          },
        },
        specialties: {
          isLoading: specialties.isLoading,
          records: specialties.data?.records || [],
        },
      },
      setData,
      isTwoWeekly,
      hiddenWeekend,
      weekColumns,
      dates: weekDates,
      calendar: {
        isLoading: calendar.isLoading,
        records: showHidden ? calendar.data?.records || [] : calendar.visibleRecords,
        withoutTeam: showHidden ? calendar.data?.withoutTeam || [] : calendar.visibleWithoutTeam,
        drafts: calendar.drafts,
        hiddenCount: calendar.hiddenCount,
        hiddenForemenCount: calendar.hiddenForemenCount,
      },
      monthly: {
        ...state.monthly,
        records: showHidden ? monthly.data?.records || {} : visibleRecords,
        drafts: monthlyDrafts,
        isLoading: monthly.isLoading,
        limit: isTwoWeekly ? TWO_WEEKLY_LIMIT : MONTHLY_LIMIT,
        first,
        last,
        hiddenCount,
        hiddenDaysCount,
      },
    }),
    [
      state,
      setState,
      jobs.isLoading,
      jobsRecords,
      jobsPagy,
      jobsPage,
      isJobsLastPage,
      jobsLoaderRef,
      specialties.isLoading,
      specialties.data?.records,
      setData,
      isTwoWeekly,
      showHidden,
      hiddenWeekend,
      weekColumns,
      weekDates,
      calendar.isLoading,
      calendar.data?.records,
      calendar.data?.withoutTeam,
      calendar.visibleRecords,
      calendar.visibleWithoutTeam,
      calendar.drafts,
      calendar.hiddenCount,
      calendar.hiddenForemenCount,
      monthly.data?.records,
      monthly.isLoading,
      visibleRecords,
      monthlyDrafts,
      first,
      last,
      hiddenCount,
      hiddenDaysCount,
    ],
  );

  return <WorkloadContext.Provider value={contextValue}>{children}</WorkloadContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkload = () => useContext(WorkloadContext);
